/// <amd-module name="TFS/FeatureManagement/TfsFeatureService" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssObservableService, IVssPageContext, Services, VssObservableService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPageService } from "VSS/Platform/Page";

import { IFeatureDefinitionData, IFeatureUpdateError, ITfsFeatureService } from "TFS/FeatureManagement/Types";
import {
    ContributedFeature,
    ContributedFeatureEnabledValue,
    ContributedFeatureSettingScope,
    ContributedFeatureState,
    ContributedFeatureStateQuery
} from "./Contracts/FeatureManagement";
import { IFeatureManagementRestClient } from "./RestClient/FeatureManagement";

// Re-export types for consumers of the service implementation
export { IFeatureDefinitionData, IFeatureUpdateError, ITfsFeatureService } from "TFS/FeatureManagement/Types";

class TfsFeatureService extends VssObservableService<ContributedFeatureState | IFeatureUpdateError, "featureStateUpdated" | "featureStateUpdateError">
    implements ITfsFeatureService {
    public getFeatureDefinitions(
        targetContributionId?: string,
        includeDevModeFeatures?: boolean,
        tagsToInclude?: string[],
        tagsToExclude?: string[]
    ): Promise<IFeatureDefinitionData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        const featureScopes: { [scopeName: string]: string } = {};
        if (tfsPageData && tfsPageData.project) {
            featureScopes["project"] = tfsPageData.project.id;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService
            .getDataAsync<IFeatureDefinitionData>("ms.vss-tfs-web.manage-features-data-provider", {
                targetContributionId,
                includeDevModeFeatures,
                featureScopes
            })
            .then(featureData => {
                const result: IFeatureDefinitionData = featureData ? { ...featureData } : { features: [], devModeFeatures: [], managementScopes: {} };
                if (tagsToInclude) {
                    if (result.features) {
                        result.features = result.features.filter(fd => fd.tags && fd.tags.some(t => tagsToInclude.indexOf(t) >= 0));
                    }

                    if (result.devModeFeatures) {
                        result.devModeFeatures = result.devModeFeatures.filter(fd => fd.tags && fd.tags.some(t => tagsToInclude.indexOf(t) >= 0));
                    }
                }

                if (tagsToExclude) {
                    if (result.features) {
                        result.features = result.features.filter(fd => !fd.tags || !fd.tags.some(t => tagsToExclude.indexOf(t) >= 0));
                    }

                    if (result.devModeFeatures) {
                        result.devModeFeatures = result.devModeFeatures.filter(fd => !fd.tags || !fd.tags.some(t => tagsToExclude.indexOf(t) >= 0));
                    }
                }

                return result;
            });
    }

    public getFeatureStates(
        features: ContributedFeature[],
        userScoped: boolean,
        settingScope: string,
        scopeValues: { [scopeName: string]: string }
    ): Promise<{ [featureId: string]: ContributedFeatureState }>[] {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        // Group the features by service, for OnPrem they all are returned from one service.
        const featuresByService: { [serviceInstanceType: string]: ContributedFeature[] } = {};

        if (pageData && pageData.isHosted) {
            for (const feature of features) {
                let serviceInstanceType = feature.serviceInstanceType;
                if (!serviceInstanceType || feature.serviceInstanceType === pageData.serviceInstanceType) {
                    serviceInstanceType = "";
                }
                let featuresForService = featuresByService[serviceInstanceType];
                if (!featuresForService) {
                    featuresForService = [];
                    featuresByService[serviceInstanceType] = featuresForService;
                }
                featuresForService.push(feature);
            }
        } else {
            featuresByService[""] = features;
        }

        // Retrieve each of the states from their respective services.
        const servicePromises: Promise<{ [featureId: string]: ContributedFeatureState }>[] = [];
        const userScope = userScoped ? "me" : "host";

        for (const serviceInstanceType in featuresByService) {
            const query: ContributedFeatureStateQuery = {
                featureIds: featuresByService[serviceInstanceType].map(f => f.id),
                featureStates: {},
                scopeValues: scopeValues
            };

            const featureManagementClient = this.pageContext.getRestClient<IFeatureManagementRestClient>("IFeatureManagementRestClient", {
                serviceInstanceType: serviceInstanceType
            });
            let queryPromise: Promise<ContributedFeatureStateQuery>;

            // If a specific setting scope was supplied supply the scope and its value, otherwise get the default scope.
            if (settingScope) {
                queryPromise = featureManagementClient.queryFeatureStatesForNamedScope(query, userScope, settingScope, scopeValues[settingScope]);
            } else {
                queryPromise = featureManagementClient.queryFeatureStatesForDefaultScope(query, userScope);
            }

            // When this completes convert the result into a dictionary and resolve the promise.
            // The resulting dictionary is returned through the collection service promises.
            servicePromises.push(
                new Promise<{ [featureId: string]: ContributedFeatureState }>((resolve, reject) => {
                    queryPromise.then(queryResult => {
                        const featureStates: { [featureId: string]: ContributedFeatureState } = {};

                        if (queryResult.featureStates) {
                            for (const featureId in queryResult.featureStates) {
                                featureStates[featureId] = queryResult.featureStates[featureId];
                            }
                        }
                        resolve(featureStates);
                    }, reject);
                })
            );
        }

        return servicePromises;
    }

    public setFeatureState(
        feature: ContributedFeature,
        userScoped: boolean,
        settingScope: string | undefined,
        scopeValue: string | undefined,
        state: ContributedFeatureEnabledValue,
        reason?: string,
        reasonCode?: string
    ): Promise<ContributedFeatureState> {
        const featureManagementClient = this.pageContext.getRestClient<IFeatureManagementRestClient>("IFeatureManagementRestClient", {
            serviceInstanceType: feature.serviceInstanceType
        });

        const featureState = {
            featureId: feature.id,
            scope: {
                settingScope: settingScope,
                userScoped: userScoped
            },
            state: state
        } as ContributedFeatureState;

        if (settingScope) {
            return featureManagementClient
                .setFeatureStateForScope(featureState, feature.id, userScoped ? "me" : "host", settingScope, scopeValue!, reason, reasonCode)
                .then(
                    featureState => {
                        this._notify(featureState, "featureStateUpdated");
                        return featureState;
                    },
                    reason => {
                        this._notify({ feature: feature, error: reason as Error }, "featureStateUpdateError");
                        throw reason;
                    }
                );
        } else {
            return featureManagementClient.setFeatureState(featureState, feature.id, userScoped ? "me" : "host", reason, reasonCode).then(
                featureState => {
                    this._notify(featureState, "featureStateUpdated");
                    return featureState;
                },
                reason => {
                    this._notify({ feature: feature, error: reason as Error }, "featureStateUpdateError");
                    throw reason;
                }
            );
        }
    }
}

Services.add("ITfsFeatureService", { serviceFactory: TfsFeatureService });
